import { useEffect, useRef } from "react";

import { LockOutlined, VisibilityOutlined } from "@mui/icons-material";
import { Chip, Link, Tooltip, Typography } from "@mui/material";
import { isNil } from "lodash";
import { usePostHog } from "posthog-js/react";

import { useSubscriptionStatus } from "hooks";
import {
  captureCarrierUpgradeCtaClicked,
  ISOStringToRelativeTime,
} from "utils";

import {
  CARRIER_UPGRADE_RELATIVE_URL,
  CarrierUpgradeSource,
  ClientAppEvent,
  EVOTRUX_BLUE,
} from "../../constants";

import type { SxProps, Theme } from "@mui/material";

interface UpgradeIndicatorProps {
  quoteId: number;
  sx?: SxProps<Theme>;
}

function UpgradeIndicator(props: UpgradeIndicatorProps) {
  const { quoteId, sx } = props;
  const posthog = usePostHog();

  return (
    <Tooltip
      title={
        <Typography variant="body2">
          Upgrade your plan to see when the shipper last viewed your quote and
          more.
        </Typography>
      }
      sx={sx}
      componentsProps={{ tooltip: { sx: { pointerEvents: "auto" } } }}
    >
      <Link
        href={CARRIER_UPGRADE_RELATIVE_URL}
        target="_blank"
        rel="noopener"
        color="inherit"
        onClick={(event) => {
          event.stopPropagation();
          captureCarrierUpgradeCtaClicked(
            posthog,
            CarrierUpgradeSource.LAST_VIEWED_QUOTE,
            { quote_id: quoteId },
          );
        }}
      >
        <Chip
          icon={<LockOutlined fontSize="small" />}
          label={
            <Typography variant="body2">
              {`Shipper last viewed `}
              <Typography
                component="span"
                variant="body2"
                sx={{ color: EVOTRUX_BLUE, textDecoration: "underline" }}
              >
                Upgrade
              </Typography>
            </Typography>
          }
          variant="filled"
          sx={sx}
        />
      </Link>
    </Tooltip>
  );
}

interface Props {
  quoteId: number;
  viewedAt: string | null;
  hideFromNonSubscribed?: boolean;
  sx?: SxProps<Theme>;
}

export function ShipperViewedIndicator(props: Props) {
  const { quoteId, viewedAt, hideFromNonSubscribed = false, sx } = props;
  const { isPayingCarrier } = useSubscriptionStatus();
  const posthog = usePostHog();
  const hasTrackedView = useRef(false);

  const showsRealChip = isPayingCarrier && !isNil(viewedAt);

  useEffect(() => {
    hasTrackedView.current = false;
  }, [quoteId]);

  useEffect(() => {
    if (showsRealChip && !hasTrackedView.current) {
      hasTrackedView.current = true;
      posthog?.capture(
        ClientAppEvent.CARRIER_QUOTE_LAST_VIEWED_INDICATOR_VIEWED,
        { quote_id: quoteId },
      );
    }
  }, [showsRealChip, quoteId, posthog]);

  if (!isPayingCarrier && hideFromNonSubscribed) {
    return null;
  }

  if (!isPayingCarrier) {
    return <UpgradeIndicator quoteId={quoteId} sx={sx} />;
  }

  if (isNil(viewedAt)) {
    return null;
  }

  return (
    <Chip
      icon={<VisibilityOutlined fontSize="small" />}
      label={
        <Typography variant="body2">
          {`Shipper last viewed ${ISOStringToRelativeTime(viewedAt)}`}
        </Typography>
      }
      variant="filled"
      sx={sx}
    />
  );
}
